import { useEffect, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  ALERT_CHANNELS,
  ALERT_CHANNEL_LABELS,
  SMS_AVAILABLE,
  isLockedChannel,
  type AlertChannel,
} from "../data/alert-channel-policy";
import {
  accountFloorSentence,
  changedMemberPreferenceRows,
  fetchMemberAlertChannelPreferencesView,
  memberAlertEventDefinition,
  memberAlertEventLabel,
  personalEmailAvailable,
  quietHoursSentence,
  upsertMyAlertChannelPreference,
  type AccountAlertFloor,
  type MemberAlertChannelPreferenceRow,
  type MemberAlertChannelPreferencesView,
} from "../data/member-alert-channel-preferences";
import { useReadModel } from "../data/useReadModel";

// Admin -> My alert channels. The self-service half of the alert story: what THIS member wants on
// top of the account floor set in "Alerts & channel policies" next door.
//
// Same rows-are-events matrix as that panel, deliberately, so the two read as one idea. Three
// things make this surface different, and all three are honesty constraints rather than layout:
//
//   1. No capability gate. Every active member owns their own preferences; the RPC binds the row
//      to auth.uid() and refuses anything else, so there is nothing here to gate on.
//   2. Switching a cell OFF removes only this member's own addition. The copy says so at the top
//      instead of leaving a member to assume they have silenced an account-wide channel.
//   3. Safety cells are not editable at all: SOS and duress deliver in-app and by push whatever
//      this page says, and personal email for them is refused by the server.
//
// It does not poll. A personal form has one writer - the person looking at it - and a background
// refetch would replace their in-progress edits with the server's copy.

export function MemberAlertChannelPreferencesPanel({
  client,
  accountId,
  canViewAccountFloor = false,
}: {
  client: SupabaseClient;
  accountId: string;
  /**
   * policy.view capability. Only then is the account floor read and shown as read-only
   * explanation; this panel never writes account policy.
   */
  canViewAccountFloor?: boolean;
}) {
  const [reloadNonce, setReloadNonce] = useState(0);
  const { status, data, error } = useReadModel<MemberAlertChannelPreferencesView>(
    () => fetchMemberAlertChannelPreferencesView(client, accountId, canViewAccountFloor),
    [client, accountId, canViewAccountFloor, reloadNonce],
  );

  if (status === "loading" && data === null) {
    return (
      <p className="surface__state" role="status">
        Loading your alert channels...
      </p>
    );
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load your alert channel preferences."}
      </p>
    );
  }
  if (data === null) {
    return null;
  }
  if (data.preferences.status === "unavailable") {
    return (
      <p className="surface__state" role="status">
        {data.preferences.reason}
      </p>
    );
  }
  if (data.preferences.rows.length === 0) {
    return (
      <p className="surface__state" role="status">
        No alert events are available for your account yet.
      </p>
    );
  }

  return (
    <MemberAlertPreferencesEditor
      client={client}
      accountId={accountId}
      rows={data.preferences.rows}
      floor={data.floor}
      onSaved={() => setReloadNonce((nonce) => nonce + 1)}
    />
  );
}

/** Baseline after the server accepts one event. Rows that have not been accepted stay put. */
function acceptSavedPreferenceRow(
  baseline: readonly MemberAlertChannelPreferenceRow[],
  saved: MemberAlertChannelPreferenceRow,
): readonly MemberAlertChannelPreferenceRow[] {
  const index = baseline.findIndex((row) => row.eventKey === saved.eventKey);
  if (index < 0) return [...baseline, saved];
  const next = baseline.slice();
  next[index] = saved;
  return next;
}

function MemberAlertPreferencesEditor({
  client,
  accountId,
  rows,
  floor,
  onSaved,
}: {
  client: SupabaseClient;
  accountId: string;
  rows: readonly MemberAlertChannelPreferenceRow[];
  floor: AccountAlertFloor | null;
  onSaved: () => void;
}) {
  const [draftRows, setDraftRows] = useState<readonly MemberAlertChannelPreferenceRow[]>(rows);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);
  // Last snapshot this form accepted. A refresh is adopted only when the draft still matches it.
  // Comparing the draft to the incoming payload instead makes a pristine form look dirty and
  // write that stale draft back over the refresh.
  const baselineRowsRef = useRef(rows);
  const draftRowsRef = useRef(draftRows);
  draftRowsRef.current = draftRows;

  useEffect(() => {
    if (changedMemberPreferenceRows(baselineRowsRef.current, draftRowsRef.current).length === 0) {
      baselineRowsRef.current = rows;
      setDraftRows(rows);
    }
  }, [rows]);

  const dirty = changedMemberPreferenceRows(baselineRowsRef.current, draftRows).length > 0;
  const quietHours = floor === null ? null : quietHoursSentence(floor.quietHours);

  function setChannel(eventKey: string, channel: AlertChannel, next: boolean): void {
    setDraftRows((current) =>
      current.map((row) => (row.eventKey === eventKey ? { ...row, [channel]: next } : row)),
    );
    setFailure(null);
    setReceipt(null);
  }

  function discard(): void {
    baselineRowsRef.current = rows;
    setDraftRows(rows);
    setFailure(null);
    setReceipt(null);
  }

  async function submit(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    setReceipt(null);

    // One RPC per changed event row: the server owns the per-key refusals, so a partial failure
    // must name the event that stopped rather than claim everything saved. The diff is against
    // the accepted baseline, not the latest payload, so a refresh during an edit is not written
    // back. Each row the server accepts advances that row's baseline immediately. A later refusal
    // must not leave the saved row looking like the old baseline: undoing it and retrying would
    // then skip the undo and still report success.
    const savedRows = baselineRowsRef.current;
    let acceptedRows = savedRows;
    for (const row of changedMemberPreferenceRows(savedRows, draftRows)) {
      const outcome = await upsertMyAlertChannelPreference(client, accountId, row);
      if (!outcome.ok) {
        baselineRowsRef.current = acceptedRows;
        setFailure(`${memberAlertEventLabel(row.eventKey)}: ${outcome.reason}`);
        setBusy(false);
        onSaved();
        return;
      }
      acceptedRows = acceptSavedPreferenceRow(acceptedRows, row);
      baselineRowsRef.current = acceptedRows;
    }

    baselineRowsRef.current = draftRows;
    setReceipt("Your notification choices are saved.");
    setBusy(false);
    onSaved();
  }

  return (
    <div className="operator-panel alerts-policy">
      <p className="alerts-policy__floor">
        <strong>Most choices add delivery.</strong> They sit on top of what the account sends.
        Messages is different: switching Messages push off stops new Direct and Crew lock-screen
        notifications for you.
      </p>
      <p className="alerts-policy__quiet-exception">
        SOS and duress always reach you in-app and by push, whatever is set here. Personal email is
        not available for those two events.
      </p>
      {quietHours !== null ? <p className="alerts-policy__card-body">{quietHours}</p> : null}

      <table className="commercial__table alerts-policy__matrix">
        <caption className="alerts-policy__caption">
          Your notification choices - {draftRows.length}{" "}
          {draftRows.length === 1 ? "event" : "events"} · {ALERT_CHANNELS.length} channels
        </caption>
        <thead>
          <tr>
            <th scope="col">Event</th>
            {ALERT_CHANNELS.map((channel) => (
              <th key={channel} scope="col" className="alerts-policy__col">
                {ALERT_CHANNEL_LABELS[channel]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {draftRows.map((row) => {
            const definition = memberAlertEventDefinition(row.eventKey);
            const floorRow =
              floor === null
                ? undefined
                : floor.rows.find((candidate) => candidate.eventKey === row.eventKey);
            const floorNote = floor === null ? null : accountFloorSentence(floorRow);
            return (
              <tr key={row.eventKey}>
                <th scope="row" className="alerts-policy__event">
                  <span className="alerts-policy__event-name">{memberAlertEventLabel(row.eventKey)}</span>
                  {definition !== "" ? (
                    <span className="alerts-policy__event-def">{definition}</span>
                  ) : null}
                  {floorNote !== null ? (
                    <span className="alerts-policy__event-floor">{floorNote}</span>
                  ) : null}
                </th>
                {ALERT_CHANNELS.map((channel) => (
                  <td key={channel} className="alerts-policy__cell">
                    <PreferenceSwitch
                      channel={channel}
                      eventKey={row.eventKey}
                      checked={row[channel]}
                      disabled={busy}
                      onChange={(next) => setChannel(row.eventKey, channel, next)}
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <ul className="alerts-policy__legend" aria-label="Switch states">
        <li>
          <span className="alerts-policy__sw alerts-policy__sw--locked" aria-hidden="true" />
          Always on - safety delivery you cannot change
        </li>
        <li>
          <span className="alerts-policy__sw alerts-policy__sw--on" aria-hidden="true" />
          On - your choice is enabled
        </li>
        <li>
          <span className="alerts-policy__sw" aria-hidden="true" />
          Off - your choice is disabled
        </li>
        <li>
          <span className="alerts-policy__sw alerts-policy__sw--unavailable" aria-hidden="true" />
          Not available - SMS anywhere, email on SOS, duress, and Messages
        </li>
      </ul>

      <div className="policy__editor-actions">
        <button
          type="button"
          className="minter__confirm-go"
          disabled={busy || !dirty}
          onClick={() => void submit()}
        >
          {busy ? "Saving..." : "Save changes"}
        </button>
        <button type="button" className="builder__navbtn" disabled={busy || !dirty} onClick={discard}>
          Discard changes
        </button>
      </div>

      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="surface__state surface__state--ok" role="status">
          {receipt}
        </p>
      ) : null}
    </div>
  );
}

/**
 * One matrix cell. Four states, matching the legend: always-on (safety delivery the resolver
 * forces), on, off, and unavailable. Unavailable covers two different server refusals - SMS has no
 * delivery path at all, and personal email is refused for SOS and duress - so each says which,
 * rather than rendering as a greyed-out switch that reads as broken.
 */
function PreferenceSwitch({
  channel,
  eventKey,
  checked,
  disabled,
  onChange,
}: {
  channel: AlertChannel;
  eventKey: string;
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  const name = `${ALERT_CHANNEL_LABELS[channel]} for ${memberAlertEventLabel(eventKey)}`;

  if (channel === "sms" && !SMS_AVAILABLE) {
    return (
      <span
        className="alerts-policy__sw alerts-policy__sw--unavailable"
        role="img"
        aria-label={`${name}: coming soon`}
      />
    );
  }

  if (isLockedChannel(eventKey, channel)) {
    return (
      <span
        className="alerts-policy__sw alerts-policy__sw--locked"
        role="img"
        aria-label={`${name}: always on`}
      />
    );
  }

  if (channel === "email" && !personalEmailAvailable(eventKey)) {
    return (
      <span
        className="alerts-policy__sw alerts-policy__sw--unavailable"
        role="img"
        aria-label={`${name}: not available`}
      />
    );
  }

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={name}
      disabled={disabled}
      className={`alerts-policy__sw alerts-policy__sw--button ${checked ? "alerts-policy__sw--on" : ""}`}
      onClick={() => onChange(!checked)}
    />
  );
}
